import axios from 'axios';
import {
  GotyRankings,
  UserGotySelection,
  UserGotySelectionsResponse,
  EligibleGamesResponse,
  GotyCategory,
  UserGotyListResponse,
} from '../types/Goty';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// リクエストインターセプター（トークン追加）
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * GOTY投票ランキングを取得
 */
export const getGotyRankings = async (year: number): Promise<GotyRankings> => {
  const response = await api.get(`/goty/${year}/rankings/`);
  return response.data;
};

/**
 * 自分のGOTY選択を取得
 */
export const getMyGotySelections = async (year: number, isMyGoty: boolean = false): Promise<{ year: number; selections: UserGotySelection[] }> => {
  const response = await api.get(`/goty/${year}/my-selections/`, {
    params: { is_my_goty: isMyGoty },
  });
  return response.data;
};

/**
 * GOTY選択を作成/更新
 */
export const createOrUpdateGotySelection = async (
  year: number,
  category: GotyCategory,
  gameId: number,
  comment: string = '',
  isMyGoty: boolean = false
): Promise<UserGotySelection> => {
  const response = await api.post(`/goty/${year}/my-selections/`, {
    category,
    game_id: gameId,
    comment,
    is_my_goty: isMyGoty,
  });
  return response.data;
};

/**
 * GOTY選択を削除
 */
export const deleteGotySelection = async (year: number, category: GotyCategory, isMyGoty: boolean = false): Promise<void> => {
  await api.delete(`/goty/${year}/my-selections/`, {
    data: { category, is_my_goty: isMyGoty },
  });
};

/**
 * 特定ユーザーのGOTY選択を取得（公開プロフィール用）
 */
export const getUserGotySelections = async (
  username: string,
  year: number,
  isMyGoty: boolean = false
): Promise<UserGotySelectionsResponse> => {
  const response = await api.get(`/goty/${year}/${username}/`, {
    params: { is_my_goty: isMyGoty },
  });
  return response.data;
};

/**
 * GOTY対象ゲーム一覧を取得
 */
export const getEligibleGames = async (
  year: number,
  query: string = '',
  page: number = 1
): Promise<EligibleGamesResponse> => {
  const response = await api.get(`/goty/${year}/eligible-games/`, {
    params: { query, page },
  });
  return response.data;
};

/**
 * ユーザーのGOTY選択にいいね
 */
export const likeUserGotySelections = async (username: string, year: number, isMyGoty: boolean = false): Promise<{ liked: boolean }> => {
  const response = await api.post(`/goty/${year}/${username}/like/`, null, {
    params: { is_my_goty: isMyGoty },
  });
  return response.data;
};

/**
 * ユーザーのGOTY選択のいいねを解除
 */
export const unlikeUserGotySelections = async (username: string, year: number, isMyGoty: boolean = false): Promise<{ liked: boolean }> => {
  const response = await api.delete(`/goty/${year}/${username}/like/`, {
    params: { is_my_goty: isMyGoty },
  });
  return response.data;
};

/**
 * ユーザーが作成したGOTYの年リストを取得（ProfilePage用）
 */
export const getUserGotyList = async (username: string): Promise<UserGotyListResponse> => {
  const response = await api.get(`/goty/user/${username}/`);
  return response.data;
};
